import { InspModelItem, RuleItem } from "@types";
import { Edit, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { getInspRules } from "../../services/inspectService";
import RuleDetailModal from "./RuleDetailModal";
import { RuleCreateModal } from "./RuleCreateModal";
import { deleteInspRule } from "../../services/inspRuleService";
import { toast } from "react-toastify";
import {
  Box,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  ButtonBase,
} from "@mui/material";
import { EditIcon } from "common-lib";
import { t } from "i18next";

interface Props {
  modelList: InspModelItem[];
  ruleList: RuleItem[];
  setRuleList: React.Dispatch<React.SetStateAction<RuleItem[]>>;
}

export function RuleTable({ modelList, ruleList, setRuleList }: Props) {
  // const [ruleList, setRuleList] = useState<RuleItem[] | null>(null);
  const [selectedRule, setSelectedRule] = useState<RuleItem | null>(null);
  const [openRuleModal, setOpenRuleModal] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageGroupStart = Math.floor((page - 1) / 5) * 5 + 1;
  const pageGroupEnd = Math.min(pageGroupStart + 4, totalPages);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    rule: RuleItem;
  } | null>(null);

  const visiblePages = Array.from({ length: pageGroupEnd - pageGroupStart + 1 }, (_, i) => pageGroupStart + i);

  const handleCreated = (newRule: RuleItem) => {
    setRuleList((prev) => [newRule, ...prev]);
  };

  // useEffect(() => {
  //   const fetchData = async () => {
  //     const ruleData = await getInspRules();
  //     console.log(ruleData);
  //     setRuleList(ruleData);
  //   };
  //   fetchData();
  // }, []);

  const handleOpenRule = (rule: RuleItem) => {
    setSelectedRule(rule);
    setOpenRuleModal(true);
  };

  const handleCloseRule = () => {
    setOpenRuleModal(false);
    setSelectedRule(null);
  };

  const handleContextMenu = (e: React.MouseEvent, rule: RuleItem) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, rule });
  };

  const handleDeleteRule = async () => {
    if (!contextMenu) return;
    if (window.confirm(t("DeleteConfirmMsg"))) {
      try {
        await deleteInspRule(contextMenu.rule.id); // API 함수 교체
        setRuleList((prev) => prev.filter((r) => r.id !== contextMenu.rule.id));
        setContextMenu(null);
      } catch (error) {
        toast.error(t("DeleteFailMsg"));
      }
    }
  };

  const handleUpdated = (updatedRule: RuleItem) => {
    setRuleList((prev) => prev.map((r) => (r.id === updatedRule.id ? updatedRule : r)));
  };

  return (
    <>
      <div className="flex justify-end mb-3">
        <button
          onClick={() => setOpenCreateModal(true)}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition"
        >
          <Plus className="w-4 h-4" />
          {t("AddRule")}
        </button>
      </div>
      <Box>
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", borderColor: "grey.200" }}>
          <TableContainer sx={{ maxHeight: 480, overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary", py: 1.25, bgcolor: "grey.50" }}
                  >
                    {t("InspRuleName")}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary", py: 1.25, bgcolor: "grey.50" }}
                  >
                    {t("Model")}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary", py: 1.25, bgcolor: "grey.50" }}
                  >
                    {t("Description")}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {ruleList && ruleList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: "center", py: 5, color: "text.secondary", fontSize: 13 }}>
                      {t("NoInspRuleMsg")}
                    </TableCell>
                  </TableRow>
                ) : (
                  ruleList?.map((rule) => (
                    <TableRow
                      key={rule.id}
                      onClick={() => handleOpenRule(rule)}
                      onContextMenu={(e) => handleContextMenu(e, rule)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { bgcolor: "grey.50" },
                        "& td": { borderBottom: "0.5px solid", borderColor: "grey.200" },
                      }}
                    >
                      <TableCell sx={{ py: 1.25 }}>
                        <Typography fontSize={13} fontWeight={500}>
                          {rule.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        <Typography fontSize={12} color="text.secondary">
                          {rule.inspModel.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        <Typography
                          fontSize={12}
                          color="text.secondary"
                          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 240 }}
                        >
                          {rule.description || "-"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {contextMenu && (
          <>
            <Box sx={{ position: "fixed", inset: 0, zIndex: 1299 }} onClick={() => setContextMenu(null)} />
            <Paper
              elevation={0}
              sx={{
                position: "fixed",
                top: contextMenu.y,
                left: contextMenu.x,
                zIndex: 1300,
                minWidth: 120,
                py: 0.5,
                border: "0.5px solid",
                borderColor: "grey.200",
                borderRadius: 1.5,
                boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
              }}
            >
              <ButtonBase
                onClick={handleDeleteRule}
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  fontSize: 13,
                  color: "error.main",
                  justifyContent: "flex-start",
                  "&:hover": { bgcolor: "error.50" },
                }}
              >
                <Trash2 size={14} />
                {t("Delete")}
              </ButtonBase>
            </Paper>
          </>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.5, mt: 2.5 }}>
          <PaginationButton onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            {t("Previous")}
          </PaginationButton>

          {pageGroupStart > 1 && <PaginationButton onClick={() => setPage(pageGroupStart - 1)}>...</PaginationButton>}

          {visiblePages.map((p) => (
            <PaginationButton key={p} onClick={() => setPage(p)} active={page === p}>
              {p}
            </PaginationButton>
          ))}

          {pageGroupEnd < totalPages && (
            <PaginationButton onClick={() => setPage(pageGroupEnd + 1)}>...</PaginationButton>
          )}

          <PaginationButton onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
            {t("Next")}
          </PaginationButton>
        </Box>

        {selectedRule && (
          <RuleDetailModal
            rule={selectedRule}
            modelList={modelList}
            open={openRuleModal}
            onClose={handleCloseRule}
            onUpdated={handleUpdated}
          />
        )}
      </Box>
      <RuleCreateModal
        modelList={modelList}
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        onCreated={handleCreated}
      />
    </>
  );
}

function PaginationButton({
  onClick,
  disabled,
  active,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box
      component="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        minWidth: 32,
        height: 32,
        px: 1,
        fontSize: 12,
        borderRadius: 1,
        border: "0.5px solid",
        borderColor: active ? "primary.main" : "grey.300",
        bgcolor: active ? "primary.main" : "transparent",
        color: active ? "#fff" : "text.secondary",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.4 : 1,
        "&:hover": { bgcolor: active ? "primary.main" : "grey.50" },
      }}
    >
      {children}
    </Box>
  );
}
